// components/Orders/OrderNextStep.js
// ─────────────────────────────────────────────────────────────────────────────
// "What do I do next with this order?" — one banner, every vendor type.
//
// Every vendor order-details page (restaurant, retailer/wholesaler, healthcare,
// brand) runs the SAME delivery leg: assign a rider or a 3PL partner, hand the
// goods over, dispatch, deliver. They differ only in what sits above that leg
// (a kitchen, a shelf, a dispensary, a print job). So the next-step guidance is
// shared, and it reads from utilities/orderDeliverySteps.js — the one place
// that knows all ten delivery_status members.
//
// Render it next to whatever progress indicator the page already has. It is
// deliberately self-contained (inline styles, no page CSS beyond the optional
// .rod-next-step* responsive rules) so it can be dropped into pages with very
// different layouts.
// ─────────────────────────────────────────────────────────────────────────────
import React from "react";
import { MdCancel, MdCheck } from "react-icons/md";
import {
  getDeliveryStep,
  WAITING_ON,
  AUDIENCE,
} from "../../utilities/orderDeliverySteps";
import "./OrderNextStep.css";

const toneFor = (step) => {
  if (step.isCancelled) {
    return { bg: "#fdecea", border: "#f5c2bd", fg: "#a4291d", dot: "#dc3545" };
  }
  if (step.isTerminal) {
    return { bg: "#eaf7ee", border: "#bfe3ca", fg: "#1e6b34", dot: "#28a745" };
  }
  // Navy = the vendor has to act. Amber = it is out of their hands.
  if (step.waitingOn === WAITING_ON.VENDOR) {
    return { bg: "#eef4f9", border: "#c6d9e8", fg: "#013A63", dot: "#013A63" };
  }
  return { bg: "#fdf3e7", border: "#f2d5ae", fg: "#8a4b08", dot: "#e67e22" };
};

// An admin is never the one holding the order, so the vendor's "Your turn"
// becomes "With the vendor". The other two read the same either way.
const waitingLabelFor = (step) => {
  switch (step.waitingOn) {
    case WAITING_ON.RIDER:
      return "With the rider";
    case WAITING_ON.PARTNER:
      return "With the partner";
    case WAITING_ON.VENDOR:
      return step.audience === AUDIENCE.ADMIN ? "With the vendor" : "Your turn";
    default:
      return null;
  }
};

/**
 * @param {object}  props.order      the order document as the details endpoint sends it
 * @param {string} [props.audience]  AUDIENCE.VENDOR (default) or AUDIENCE.ADMIN.
 *                                   Admin surfaces report what is happening;
 *                                   vendor surfaces instruct.
 * @param {object} [props.style]     extra styles merged onto the banner wrapper
 * @param {string} [props.className]
 */
const OrderNextStep = ({ order, audience = AUDIENCE.VENDOR, style, className }) => {
  // Nothing to advise on until the order has actually loaded.
  if (!order) return null;

  const step = getDeliveryStep(order, { audience });
  const tone = toneFor(step);
  const waitingLabel = waitingLabelFor(step);
  const closed = step.isTerminal || step.isCancelled;

  return (
    <div
      className={["rod-next-step", className].filter(Boolean).join(" ")}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        padding: "10px 12px",
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: "6px",
        ...style,
      }}
    >
      <div
        style={{
          flex: "none",
          width: 22,
          height: 22,
          borderRadius: "50%",
          backgroundColor: tone.dot,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "11px",
          fontWeight: 700,
          marginTop: "1px",
        }}
      >
        {step.isCancelled ? (
          <MdCancel style={{ fontSize: "14px" }} />
        ) : step.isTerminal ? (
          <MdCheck style={{ fontSize: "14px" }} />
        ) : (
          step.stage + 1
        )}
      </div>

      <div style={{ flexGrow: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: "13px",
            fontWeight: 700,
            color: tone.fg,
            lineHeight: 1.3,
          }}
        >
          {closed || step.audience === AUDIENCE.ADMIN
            ? step.title
            : `Next: ${step.title}`}
        </div>
        <div
          className="rod-next-step-detail"
          style={{
            fontSize: "12px",
            color: "#5a6570",
            lineHeight: 1.45,
            marginTop: "2px",
          }}
        >
          {step.detail}
        </div>
      </div>

      {waitingLabel && !closed && (
        <span
          className="rod-next-step-chip"
          style={{
            flex: "none",
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: tone.fg,
            backgroundColor: "#fff",
            border: `1px solid ${tone.border}`,
            borderRadius: "999px",
            padding: "4px 10px",
            whiteSpace: "nowrap",
            marginTop: "1px",
          }}
        >
          {waitingLabel}
        </span>
      )}
    </div>
  );
};

export default OrderNextStep;
